if (!customElements.get('collection-sort-dropdown')) {
  customElements.define(
    'collection-sort-dropdown',
    class CollectionSortDropdown extends HTMLElement {
      connectedCallback() {
        if (this.dataset.initialized === 'true') return;

        this.select = this.querySelector('.gl-collection-sort__native');
        this.trigger = this.querySelector('.gl-collection-sort__trigger');
        this.triggerLabel = this.querySelector('[data-sort-label]');
        this.listbox = this.querySelector('.gl-collection-sort__listbox');

        if (!this.select || !this.trigger || !this.triggerLabel || !this.listbox) return;

        this.renderOptions();
        this.trigger.hidden = false;
        this.classList.add('is-enhanced');
        this.dataset.initialized = 'true';

        this.trigger.addEventListener('click', () => this.toggle());
        this.trigger.addEventListener('keydown', (event) => this.onTriggerKeydown(event));
        this.listbox.addEventListener('click', (event) => this.onOptionClick(event));
        this.listbox.addEventListener('keydown', (event) => this.onListboxKeydown(event));
        document.addEventListener('click', this.onDocumentClick);
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick);
      }

      renderOptions() {
        this.listbox.replaceChildren(
          ...Array.from(this.select.options, (nativeOption, index) => {
            const option = document.createElement('button');
            option.type = 'button';
            option.className = 'gl-collection-sort__option';
            option.dataset.value = nativeOption.value;
            option.dataset.index = index;
            option.id = `${this.listbox.id}-option-${index}`;
            option.setAttribute('role', 'option');
            option.setAttribute('aria-selected', nativeOption.selected ? 'true' : 'false');
            option.tabIndex = -1;
            option.textContent = nativeOption.textContent.trim();
            return option;
          })
        );

        this.updateSelection();
      }

      updateSelection() {
        const selectedOption = this.select.selectedOptions[0];
        if (!selectedOption) return;

        this.triggerLabel.textContent = selectedOption.textContent.trim();
        this.options.forEach((option) => {
          option.setAttribute('aria-selected', option.dataset.value === selectedOption.value ? 'true' : 'false');
        });
      }

      get options() {
        return Array.from(this.listbox.querySelectorAll('[role="option"]'));
      }

      get isOpen() {
        return this.trigger.getAttribute('aria-expanded') === 'true';
      }

      open(focusIndex = this.select.selectedIndex) {
        this.listbox.hidden = false;
        this.trigger.setAttribute('aria-expanded', 'true');
        this.classList.add('is-open');
        this.focusOption(focusIndex);
      }

      close({ restoreFocus = false } = {}) {
        this.listbox.hidden = true;
        this.trigger.setAttribute('aria-expanded', 'false');
        this.classList.remove('is-open');
        if (restoreFocus) this.trigger.focus();
      }

      toggle() {
        if (this.isOpen) {
          this.close();
        } else {
          this.open();
        }
      }

      focusOption(index) {
        const options = this.options;
        if (!options.length) return;

        const boundedIndex = Math.max(0, Math.min(index, options.length - 1));
        options[boundedIndex].focus();
      }

      selectOption(option) {
        if (!option) return;

        const valueChanged = this.select.value !== option.dataset.value;
        this.select.value = option.dataset.value;
        this.updateSelection();
        this.close({ restoreFocus: true });

        if (valueChanged) {
          this.select.dispatchEvent(new Event('input', { bubbles: true }));
        }
      }

      onTriggerKeydown(event) {
        const selectedIndex = Math.max(this.select.selectedIndex, 0);

        if (event.key === 'ArrowDown') {
          event.preventDefault();
          this.open(selectedIndex);
        } else if (event.key === 'ArrowUp') {
          event.preventDefault();
          this.open(selectedIndex);
        } else if (event.key === 'Home') {
          event.preventDefault();
          this.open(0);
        } else if (event.key === 'End') {
          event.preventDefault();
          this.open(this.options.length - 1);
        }
      }

      onListboxKeydown(event) {
        const currentIndex = this.options.indexOf(document.activeElement);

        if (event.key === 'Escape') {
          event.preventDefault();
          this.close({ restoreFocus: true });
        } else if (event.key === 'ArrowDown') {
          event.preventDefault();
          this.focusOption(currentIndex + 1);
        } else if (event.key === 'ArrowUp') {
          event.preventDefault();
          this.focusOption(currentIndex - 1);
        } else if (event.key === 'Home') {
          event.preventDefault();
          this.focusOption(0);
        } else if (event.key === 'End') {
          event.preventDefault();
          this.focusOption(this.options.length - 1);
        } else if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.selectOption(document.activeElement.closest('[role="option"]'));
        } else if (event.key === 'Tab') {
          this.close();
        }
      }

      onOptionClick(event) {
        this.selectOption(event.target.closest('[role="option"]'));
      }

      onDocumentClick = (event) => {
        if (this.isOpen && !this.contains(event.target)) this.close();
      };
    }
  );
}
